import { useEffect } from 'react';

import type { DisplayCreative } from '@feathr/blackbox';
import { useStore } from '@feathr/extender/state';

/** Reset the lookalike creative ad group if a lookalike targeting is not present, preventing a validation error
 * if the user had only a lookalike audience selected before removing the lookalike targeting.
 * @param creative - The creative to reset the lookalike ad group for
 * @returns hasActiveLookalikeTargeting - Whether a lookalike targeting exists for the campaign
 */
export function useResetLookalikeCreativeAdGroup({
  creative,
}: Readonly<{ creative: DisplayCreative }>): boolean {
  const { Targetings } = useStore();
  const campaignId = creative.get('parent');

  const hasActiveLookalikeTargeting = !!Targetings.models.find(
    (t) =>
      t.get('kind') === 'omni_lookalike' &&
      t.get('is_archived') !== true &&
      t.get('parent') === campaignId,
  );

  useEffect(() => {
    if (!hasActiveLookalikeTargeting) {
      const adGroups = creative.get('ad_groups');
      const hasLookalikeAudiences = adGroups?.includes('lookalike') ?? false;

      if (!hasLookalikeAudiences) {
        return;
      }

      creative.set({ ad_groups: [] });
    }
  }, [hasActiveLookalikeTargeting, creative]);

  return hasActiveLookalikeTargeting;
}
